/**
 * The closure-drafting brief (13.2-08; D-13.2-A-2, D-13.2-P-9): plain text a Care Manager pastes into Nina (SaH) or
 * Hana (NDIS) to draft an incident's closure fields, then cross-checks in the Q&S Advisor.
 *
 * `closureBrief(row, followUps)` gives:
 *   1. the incident's facts (`BRIEF_FACT_COLUMNS`, each with its FIELDSET label);
 *   2. its follow-ups, each with its state;
 *   3. the closure questions (`BRIEF_QUESTION_COLUMNS`), in FIELDSET order with their exact labels, and the allowed
 *      answers of each choice question;
 *   4. the instruction line (`BRIEF_INSTRUCTION`).
 *
 * **What it never carries.** Contact details (`CONTACT_COLUMNS`) are never read: the brief is built from a fixed
 * list, and a load-time check refuses a list that names one. People's names (the client, witnesses, the injured
 * worker, the reporter) are not in the list either: a draft of the closure fields doesn't need them, and the brief
 * stays inside Harmony's own tools (D-13.2-P-9).
 *
 * `draftingToolsFor(unit)` names the tools for a record's `BusinessUnit`. A tool's link is shown only when its URL is
 * confirmed (`DRAFTING_TOOLS`, each with its source); with no confirmed URL the panel names the tool without a link.
 *
 * Pure: no React, no Graph, no clock, no model call.
 */

import { liveChoice } from '../config/fieldset/live';
import { choicesOf, fieldOf } from '../config/fieldset/registerFields';
import type { RecordRow } from '../types/records';
import { formatDay } from './fieldValues';
import { FOLLOW_UP_ACTION_TYPES, type FollowUp } from './followUps';
import { CONTACT_COLUMNS } from './lodgeBuilder';

// ---------------------------------------------------------------------------
// The columns
// ---------------------------------------------------------------------------

/** The incident's facts, in the order the brief gives them. */
export const BRIEF_FACT_COLUMNS = [
  'Title',
  'BusinessUnit',
  'OccurredOn',
  'Category',
  'Description',
  'ImmediateActions',
  'HarmKind',
  'HarmDescription',
  'HarmConsequences',
  'TriageNote',
  'InvestigationFindings',
] as const;

/**
 * The closure questions the Care Manager drafts (D-13.2-A-2): ready for approval's own columns in FIELDSET order
 * (less `Status` and `Category`, which are not drafted), then *Open disclosure used?*, which Handling records and
 * the closure requires. `closureBrief.test.ts` ties this list to FIELDSET, so a change there is caught.
 */
export const BRIEF_QUESTION_COLUMNS = [
  'ClosingComments',
  'SatisfactoryResolution',
  'Preventable',
  'ManagementAssessment',
  'CorrectiveActionRequired',
  'CorrectiveActionNature',
  'AffectedViewsConsidered',
  'OpenDisclosureUsed',
] as const;

for (const column of [...BRIEF_FACT_COLUMNS, ...BRIEF_QUESTION_COLUMNS]) {
  if (CONTACT_COLUMNS.has(column)) throw new Error(`closureBrief: ${column} is a contact column and can never be in the brief`);
  if (!fieldOf('incidents', column)) throw new Error(`closureBrief: ${column} is not an Incidents column`);
}

/** What a blank shows as. */
export const NOT_RECORDED = 'Not recorded';

/** The brief's last line. */
export const BRIEF_INSTRUCTION = "Draft answers to each, in Harmony's voice. Check obligations in the Q&S Advisor.";

// ---------------------------------------------------------------------------
// The brief
// ---------------------------------------------------------------------------

const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : typeof v === 'number' ? String(v) : '');

const labelOf = (column: string): string => fieldOf('incidents', column)?.label ?? column;

/** A fact's value as words: days as `D MMM YYYY`, blanks as "Not recorded". */
function valueOf(row: RecordRow, column: string): string {
  const raw = text(row.fields[column]);
  if (raw === '') return NOT_RECORDED;
  const kind = fieldOf('incidents', column)?.kind;
  if (kind === 'day-existing' || kind === 'day-new') return formatDay(raw);
  return raw;
}

/** `Label: value`, or `Label? value` when the label is a question. */
function factLine(row: RecordRow, column: string): string {
  const label = labelOf(column);
  const value = valueOf(row, column);
  return label.endsWith('?') ? `${label} ${value}` : `${label}: ${value}`;
}

function followUpLine(f: FollowUp): string {
  const what = f.row.title?.trim() || 'Untitled follow-up';
  const worker = f.row.actionType === FOLLOW_UP_ACTION_TYPES['worker-update'] ? " (the injured worker's update)" : '';
  const due = f.dueOn ? formatDay(f.dueOn) : null;
  const state =
    f.state === 'done'
      ? 'Done'
      : f.state === 'scheduled'
        ? `Scheduled, due ${due}`
        : f.state === 'overdue'
          ? `Overdue, due ${due}`
          : f.state === 'undated'
            ? 'Open, no due date'
            : 'Rejected';
  return `- ${f.row.reference}: ${what}${worker}. ${state}.`;
}

function questionLine(column: string, n: number): string {
  const spec = fieldOf('incidents', column);
  const choices = spec?.kind === 'choice' ? choicesOf('incidents', column) : [];
  return `${n}. ${labelOf(column)}${choices.length > 0 ? ` (one of: ${choices.join(', ')})` : ''}`;
}

/**
 * The brief for `row`. `followUps` is the record's follow-ups (`useFollowUps`), or `null` while they are unknown
 * (loading or failed), which the brief says rather than claiming there are none.
 */
export function closureBrief(row: RecordRow, followUps: readonly FollowUp[] | null): string {
  const title = text(row.fields.Title) || 'Untitled incident';
  const lines: string[] = [
    `Closure drafting brief: ${row.ref}, ${title}`,
    'For use inside Harmony only.',
    '',
    'The incident',
    ...BRIEF_FACT_COLUMNS.map((c) => factLine(row, c)),
    '',
    'Follow-ups',
  ];
  if (followUps === null) lines.push("The follow-ups couldn't be read. Check them on the record.");
  else if (followUps.length === 0) lines.push('None recorded.');
  else lines.push(...followUps.map(followUpLine));
  lines.push('', 'The closure questions', ...BRIEF_QUESTION_COLUMNS.map((c, i) => questionLine(c, i + 1)), '', BRIEF_INSTRUCTION);
  return lines.join('\n');
}

// ---------------------------------------------------------------------------
// The drafting tools
// ---------------------------------------------------------------------------

export type DraftingToolKey = 'nina' | 'hana' | 'advisor';

export interface DraftingTool {
  key: DraftingToolKey;
  name: string;
  /** What it is for, in a few words. */
  purpose: string;
  /** The user-facing address, or `null` when none is confirmed (the tool is then named without a link). */
  url: string | null;
}

/**
 * The tools and their user-facing addresses. Each URL is confirmed (13.2-08, 25 Oct 2026) from Andy's records:
 *   - Nina: `project_nina_deployment.md` ("Production URL"), and the sah-admin-agent repo;
 *   - Hana: `reference_hana_local_e2e_and_swa_deploy.md` (the SWA's custom domain), and the ndis-admin-agent repo;
 *   - the Q&S Advisor: `project_qs_advisor_architecture.md` ("App") and `project_qs_advisor_v1_2_polish.md`, and the
 *     harmony-semantic-search repo. (Its API host is a different address and is never linked.)
 * Change one only from a confirmed source; set it to `null` rather than guess.
 */
export const DRAFTING_TOOLS: Readonly<Record<DraftingToolKey, DraftingTool>> = {
  nina: { key: 'nina', name: 'Nina', purpose: 'Support at Home', url: 'https://nina.harmonyhomecare.com.au' },
  hana: { key: 'hana', name: 'Hana', purpose: 'NDIS', url: 'https://hana.harmonyhomecare.com.au' },
  advisor: { key: 'advisor', name: 'the Q&S Advisor', purpose: 'obligations', url: 'https://nice-island-0bdee2c1e.6.azurestaticapps.net' },
};

/** Aged-care units draft in Nina (the SaH client lists serve both, `clientDirectory.STREAM_LISTS`). */
const NINA_UNITS: readonly string[] = liveChoice('incidents', 'BusinessUnit', 'Support at Home Package', 'Brokered or Private');
const HANA_UNITS: readonly string[] = liveChoice('incidents', 'BusinessUnit', 'NDIS');

/**
 * Nina, Hana or both, by `BusinessUnit`: Nina for Support at Home and Brokered or Private, Hana for NDIS, and both for
 * the mixed units and any other (Staff, a blank unit): the Care Manager then picks.
 */
export function draftingToolsFor(unit: unknown): DraftingTool[] {
  const u = text(unit);
  if (NINA_UNITS.includes(u)) return [DRAFTING_TOOLS.nina];
  if (HANA_UNITS.includes(u)) return [DRAFTING_TOOLS.hana];
  return [DRAFTING_TOOLS.nina, DRAFTING_TOOLS.hana];
}
